import { useState, useEffect, useRef } from "react";
import { Fuel, Rocket, CheckCircle2, Sparkles, Check } from "lucide-react";
import { cn } from "@/lib/utils";

interface AnimatedProgressBarProps {
  completedSections: number;
  totalSections?: number;
}

export const AnimatedProgressBar = ({ 
  completedSections, 
  totalSections = 7 
}: AnimatedProgressBarProps) => {
  const [displayedProgress, setDisplayedProgress] = useState(0);
  const [showMilestone, setShowMilestone] = useState<string | null>(null);
  const [showParticles, setShowParticles] = useState(false);
  const prevCompletedRef = useRef(completedSections);

  const targetProgress = (completedSections / totalSections) * 100;
  const isComplete = completedSections === totalSections;

  // Animate progress bar
  useEffect(() => {
    const duration = 500;
    const startProgress = displayedProgress;
    const startTime = performance.now();

    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = startProgress + (targetProgress - startProgress) * eased;
      setDisplayedProgress(current);

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  }, [targetProgress]);

  // Milestone celebrations
  useEffect(() => {
    const prev = prevCompletedRef.current;
    const prevPercent = (prev / totalSections) * 100;
    const currentPercent = (completedSections / totalSections) * 100;

    if (prevPercent < 50 && currentPercent >= 50) {
      setShowMilestone("⛽ Halfway fueled!");
      setShowParticles(true);
      setTimeout(() => {
        setShowMilestone(null);
        setShowParticles(false);
      }, 2000);
    } else if (prevPercent < 100 && currentPercent >= 100) {
      setShowMilestone("🚀 Full tank - Ready for launch!");
      setShowParticles(true);
      setTimeout(() => {
        setShowMilestone(null);
        setShowParticles(false);
      }, 3000);
    }

    prevCompletedRef.current = completedSections;
  }, [completedSections, totalSections]);

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between text-sm">
        <div className="flex items-center gap-2">
          <Fuel className={cn(
            "w-4 h-4 transition-colors duration-300",
            isComplete ? "text-neon-green" : "text-neon-cyan"
          )} />
          <span className="font-medium">Fuel Tank</span>
        </div>
        <div className="flex items-center gap-2">
          {isComplete ? (
            <>
              <CheckCircle2 className="w-4 h-4 text-neon-green" />
              <span className="text-neon-green font-medium">Launch Ready</span>
            </>
          ) : (
            <span className="text-muted-foreground">{completedSections}/{totalSections}</span>
          )}
        </div>
      </div>

      {/* Progress bar container */}
      <div className="relative">
        {/* Background track */}
        <div className={cn(
          "h-3 rounded-full overflow-hidden",
          "bg-gradient-to-r from-muted/30 to-muted/20",
          "border border-border/30"
        )}>
          {/* Progress fill */}
          <div
            className={cn(
              "h-full rounded-full transition-all duration-500 ease-out relative",
              isComplete
                ? "bg-gradient-to-r from-neon-green via-neon-cyan to-neon-green"
                : "bg-gradient-to-r from-orange-500 via-neon-pink to-neon-cyan"
            )}
            style={{ width: `${displayedProgress}%` }}
          >
            {/* Shimmer effect */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer" />
          </div>
        </div>

        {/* Step indicators */}
        <div className="flex justify-between px-1 mt-2">
          {Array.from({ length: totalSections }).map((_, i) => {
            const isStepComplete = completedSections > i;
            const isCurrent = completedSections === i + 1;

            return (
              <div
                key={i}
                className={cn(
                  "w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold transition-all duration-300",
                  isStepComplete
                    ? "bg-neon-green/20 text-neon-green border border-neon-green/50"
                    : isCurrent
                      ? "bg-neon-cyan/20 text-neon-cyan border border-neon-cyan/50 animate-pulse"
                      : "bg-muted/20 text-muted-foreground border border-border/30"
                )}
              >
                {isStepComplete ? <Check className="w-3 h-3" /> : i + 1}
              </div>
            );
          })}
        </div>

        {/* Particles */}
        {showParticles && (
          <div className="absolute inset-0 pointer-events-none overflow-visible">
            {[...Array(12)].map((_, i) => (
              <div
                key={i}
                className={cn(
                  "absolute w-1.5 h-1.5 rounded-full",
                  i % 3 === 0 ? "bg-neon-cyan" : i % 3 === 1 ? "bg-neon-pink" : "bg-neon-green"
                )}
                style={{
                  left: `${displayedProgress}%`,
                  top: '50%',
                  animation: `confetti-fall ${0.8 + Math.random() * 0.4}s ease-out forwards`,
                  animationDelay: `${i * 50}ms`
                }}
              />
            ))}
          </div>
        )}
      </div>

      {/* Milestone message */}
      {showMilestone && (
        <div className={cn(
          "flex items-center justify-center gap-2 py-2 px-4 rounded-full",
          "bg-gradient-to-r from-neon-purple/20 to-neon-cyan/20",
          "border border-neon-cyan/30",
          "animate-fade-in"
        )}>
          <Sparkles className="w-4 h-4 text-neon-cyan animate-pulse" />
          <span className="text-sm font-medium text-neon-cyan">{showMilestone}</span>
        </div>
      )}
    </div>
  );
};
